import React, { useState, useEffect } from 'react';
import {
  Audit,
  AuditProgramme,
  AuditPlan,
  AuditDashboardStats,
  AuditStatus,
} from './types';
import { auditService } from './auditService';
import { AuditListTable } from './components/AuditListTable';
import { AuditProgrammeTab } from './components/AuditProgrammeTab';
import { AuditPlanTab } from './components/AuditPlanTab';
import { AuditDetailModal } from './components/AuditDetailModal';
import { CreateAuditModal } from './components/CreateAuditModal';
import { CreateProgrammeModal } from './components/CreateProgrammeModal';
import { AuditPlanMatrixTab } from './components/AuditPlanMatrixTab';

export const AuditsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'AUDITS' | 'PROGRAMMES' | 'PLANS' | 'CALENDAR_MATRIX'>('AUDITS');
  const [filterStatus, setFilterStatus] = useState<'ALL' | AuditStatus>('ALL');

  const [stats, setStats] = useState<AuditDashboardStats | null>(null);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [programmes, setProgrammes] = useState<AuditProgramme[]>([]);
  const [plans, setPlans] = useState<AuditPlan[]>([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [showCreateAudit, setShowCreateAudit] = useState(false);
  const [showCreateProgramme, setShowCreateProgramme] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [statsData, auditsData, progsData, plansData] = await Promise.all([
        auditService.getDashboardStats(),
        auditService.getAudits(),
        auditService.getProgrammes(),
        auditService.getPlans(),
      ]);
      setStats(statsData);
      setAudits(auditsData);
      setProgrammes(progsData);
      setPlans(plansData);
    } catch (err) {
      console.error('Error loading quality audits data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveProgramme = async (id: string) => {
    try {
      setLoading(true);
      await auditService.approveProgramme(id);
      await loadAllData();
      alert('Annual audit programme approved successfully.');
    } catch (err) {
      alert('Failed to approve programme: ' + err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Title & Header */}
      <div className="page-header-banner">
        <div className="badge">QUALITY MANAGEMENT</div>
        <h1>Quality Audits & Compliance Management</h1>
        <p>EASA Part-CAMO/145/ORO, IOSA, and ISO 9001 annual programmes, auditor independence (SoD), and dual-approval reporting.</p>
      </div>

      {/* KPI Stats Grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Audits</div>
            <div className="stat-value">{stats.totalAudits}</div>
            <div className="stat-sub">Registered audits</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">In Progress</div>
            <div className="stat-value" style={{ color: '#0284c7' }}>{stats.inProgressAudits}</div>
            <div className="stat-sub">Active fieldwork / reporting</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Scheduled</div>
            <div className="stat-value" style={{ color: '#475569' }}>{stats.scheduledAudits}</div>
            <div className="stat-sub">Upcoming audits</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Completed & Closed</div>
            <div className="stat-value" style={{ color: '#10b981' }}>{stats.completedAudits}</div>
            <div className="stat-sub">Compliance: {stats.complianceRate}%</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Open Findings</div>
            <div className="stat-value" style={{ color: '#ef4444' }}>{stats.openFindings}</div>
            <div className="stat-sub">Total findings: {stats.totalFindings}</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Active Programmes</div>
            <div className="stat-value" style={{ color: '#7c3aed' }}>{stats.activeProgrammesCount}</div>
            <div className="stat-sub">Annual schedules</div>
          </div>
        </div>
      )}

      {/* Main Tabs Navigation */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('AUDITS')}
          className={`tab-btn ${activeTab === 'AUDITS' ? 'active' : ''}`}
        >
          Audits ({audits.length})
        </button>
        <button
          onClick={() => setActiveTab('PROGRAMMES')}
          className={`tab-btn ${activeTab === 'PROGRAMMES' ? 'active' : ''}`}
        >
          Annual Programmes ({programmes.length})
        </button>
        <button
          onClick={() => setActiveTab('PLANS')}
          className={`tab-btn ${activeTab === 'PLANS' ? 'active' : ''}`}
        >
          Periodic Plans ({plans.length})
        </button>
        <button
          onClick={() => setActiveTab('CALENDAR_MATRIX')}
          className={`tab-btn ${activeTab === 'CALENDAR_MATRIX' ? 'active' : ''}`}
        >
          📅 Annual Calendar & Red Alarm Matrix
        </button>
      </div>

      {/* Tab Panels */}
      {activeTab === 'AUDITS' && (
        <AuditListTable
          audits={audits}
          loading={loading}
          filterStatus={filterStatus}
          onFilterStatusChange={setFilterStatus}
          onSelectAudit={(a) => setSelectedAudit(a)}
          onOpenCreate={() => setShowCreateAudit(true)}
        />
      )}

      {activeTab === 'PROGRAMMES' && (
        <AuditProgrammeTab
          programmes={programmes}
          loading={loading}
          onOpenCreate={() => setShowCreateProgramme(true)}
          onApproveProgramme={handleApproveProgramme}
        />
      )}

      {activeTab === 'PLANS' && (
        <AuditPlanTab
          plans={plans}
          loading={loading}
          onOpenCreate={() => alert('Audit plan generation is automatically scheduled via the Annual Programme.')}
        />
      )}

      {activeTab === 'CALENDAR_MATRIX' && (
        <AuditPlanMatrixTab />
      )}

      {/* Modals */}
      {selectedAudit && (
        <AuditDetailModal
          audit={selectedAudit}
          onClose={() => setSelectedAudit(null)}
          onRefresh={loadAllData}
        />
      )}

      {showCreateAudit && (
        <CreateAuditModal
          onClose={() => setShowCreateAudit(false)}
          onSuccess={() => {
            setShowCreateAudit(false);
            loadAllData();
          }}
        />
      )}

      {showCreateProgramme && (
        <CreateProgrammeModal
          onClose={() => setShowCreateProgramme(false)}
          onSuccess={() => {
            setShowCreateProgramme(false);
            loadAllData();
          }}
        />
      )}
    </div>
  );
};
export default AuditsPage;
